import { useState, useRef, useEffect } from 'react'
import { Loader2, ChevronDown, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { useManagerSearch } from '@/hooks/useManagerSearch'
import { useSubmitForReview } from '@/hooks/useSubmitForReview'
import { toast } from '@/stores/useToastStore'
import type { UserRead } from '@/api/shares'

interface SubmitForReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
}

// Generate initials from display_name or email
function getInitials(displayName: string | null, email: string | null): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return displayName.slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return '??'
}

// Avatar color combinations
const avatarColors = [
  { bg: 'bg-[#dbeafe]', text: 'text-[#155dfc]' },
  { bg: 'bg-[#dcfce7]', text: 'text-[#00a63e]' },
  { bg: 'bg-[#fef3c7]', text: 'text-[#d97706]' },
  { bg: 'bg-[#fce7f3]', text: 'text-[#db2777]' },
  { bg: 'bg-[#e0e7ff]', text: 'text-[#4f46e5]' },
]

function getAvatarColor(id: string | number) {
  const index =
    typeof id === 'string'
      ? id.charCodeAt(0) % avatarColors.length
      : id % avatarColors.length
  return avatarColors[index]
}

export function SubmitForReviewModal({
  open,
  onOpenChange,
  planId,
}: SubmitForReviewModalProps) {
  const [selectedReviewer, setSelectedReviewer] = useState<UserRead | null>(
    null
  )
  const [notes, setNotes] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Hooks
  const { data: managers, isLoading: isLoadingManagers } = useManagerSearch()
  const submitMutation = useSubmitForReview(planId)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectReviewer = (manager: UserRead) => {
    setSelectedReviewer(manager)
    setShowDropdown(false)
  }

  const handleSubmit = () => {
    submitMutation.mutate(
      {
        notes: notes.trim() || null,
        reviewer_id: selectedReviewer ? String(selectedReviewer.id) : null,
      },
      {
        onSuccess: () => {
          toast.success('Plan submitted for review')
          handleOpenChange(false)
        },
        onError: (error: Error) => {
          toast.error(`Failed to submit: ${error.message}`)
        },
      }
    )
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedReviewer(null)
      setNotes('')
      setShowDropdown(false)
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Submit for Review</DialogTitle>
          <DialogDescription>
            Select a reviewer and add a note to submit your plan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Reviewer dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Reviewer</label>
            <div className="relative">
              <button
                ref={buttonRef}
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className={cn(
                  'flex w-full items-center justify-between',
                  'rounded-md border border-input bg-background px-3 py-2',
                  'text-sm ring-offset-background',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'cursor-pointer'
                )}
              >
                {selectedReviewer ? (
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback
                        className={cn(
                          getAvatarColor(selectedReviewer.id).bg,
                          getAvatarColor(selectedReviewer.id).text,
                          'text-xs font-medium'
                        )}
                      >
                        {getInitials(
                          selectedReviewer.display_name,
                          selectedReviewer.email
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {selectedReviewer.display_name ||
                          selectedReviewer.email}
                      </span>
                      {selectedReviewer.display_name &&
                        selectedReviewer.email && (
                          <span className="text-xs text-muted-foreground">
                            {selectedReviewer.email}
                          </span>
                        )}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted-foreground">
                    Select a reviewer
                  </span>
                )}
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>

              {/* Dropdown list */}
              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto z-50"
                >
                  {isLoadingManagers ? (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Loading reviewers...</span>
                    </div>
                  ) : managers && managers.length > 0 ? (
                    <ul className="py-1">
                      {managers.map(manager => {
                        const colors = getAvatarColor(manager.id)
                        const isSelected = selectedReviewer?.id === manager.id
                        return (
                          <li key={manager.id}>
                            <button
                              type="button"
                              onClick={() => handleSelectReviewer(manager)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2',
                                'hover:bg-muted cursor-pointer',
                                'text-left transition-colors duration-150',
                                isSelected && 'bg-muted'
                              )}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback
                                  className={cn(
                                    colors.bg,
                                    colors.text,
                                    'text-xs font-medium'
                                  )}
                                >
                                  {getInitials(
                                    manager.display_name,
                                    manager.email
                                  )}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {manager.display_name || manager.email}
                                </p>
                                {manager.display_name && manager.email && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {manager.email}
                                  </p>
                                )}
                              </div>
                              {isSelected && (
                                <Check className="h-4 w-4 text-primary" />
                              )}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <span className="text-sm">No reviewers available</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Notes textarea */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Add a note (optional)</label>
            <Textarea
              placeholder="Add any notes for the reviewer..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="min-h-[100px] resize-none"
              maxLength={2000}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitMutation.isPending || !selectedReviewer}
            className="gap-2 bg-[#CF1F2E] hover:bg-[#b81a27]"
          >
            {submitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Submit Plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
